/**
 * VendorPay (cashless vendor billing) customer-facing API client.
 *
 * Mirrors cartApi.js / valetApi.js.  Only public, unauthenticated
 * endpoints are exposed here — the POS / fulfilment surfaces live in
 * the B2B Flutter app.  The Django backend mounts vendorpay at
 * /api/vendorpay/ in parkaddaServer.  The base is resolved through the
 * shared ``apiConfig`` — set ``REACT_APP_API_ROOT`` to point the whole
 * site at a different environment, or ``REACT_APP_VENDORPAY_API_BASE``
 * to override just this module.
 */

import { API_BASES } from "../../config/apiConfig";

const VP_BASE = API_BASES.VENDORPAY;

const parseResponse = async response => {
  const data = await response.json();
  if (!response.ok || data.success === false) {
    const msg = data.message || "Request failed";
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  }
  return data.payload;
};

const jsonFetch = async (path, options = {}) => {
  const method = (options.method || "GET").toUpperCase();
  const headers = { Accept: "application/json" };
  if (method !== "GET") {
    headers["Content-Type"] = "application/json";
  }
  const response = await fetch(`${VP_BASE}${path}`, {
    headers: Object.assign(headers, options.headers || {}),
    ...options
  });
  return parseResponse(response);
};

export const vendorpayApi = {
  /**
   * Customer order lookup.  Accepts any of:
   *   - { ref }        order reference code (most specific)
   *   - { bookingId }  returns all orders against a booking
   *   - { mobile }     recent orders for the customer's phone
   */
  lookupOrder: function (args) {
    const q = [];
    if (args && args.ref) q.push("ref=" + encodeURIComponent(args.ref));
    if (args && args.bookingId) q.push("booking_id=" + encodeURIComponent(args.bookingId));
    if (args && args.mobile) q.push("phone=" + encodeURIComponent(args.mobile));
    return jsonFetch("/customer/order-lookup/?" + q.join("&"));
  },

  /** Fetch all vendor orders for a booking (thin wrapper used by the
   *  booking details page to render a "Food & Stall orders" block). */
  listOrdersForBooking: function (bookingId) {
    return jsonFetch(
      "/customer/order-lookup/?booking_id=" + encodeURIComponent(bookingId)
    );
  },

  /** Public stall menu (no auth) — used by ``/events/stall-order`` QR landing. */
  stallStorefront: function (eventId, stallId) {
    return jsonFetch(
      "/customer/stall-storefront/?event_id=" +
        encodeURIComponent(eventId) +
        "&stall_id=" +
        encodeURIComponent(stallId),
      { method: "GET" }
    );
  },

  /** Public stall menu via short URL slug — used by ``/stall/:slug`` QR landing. */
  stallStorefrontBySlug: function (slug) {
    return jsonFetch(
      "/customer/stall-storefront/?slug=" + encodeURIComponent(slug),
      { method: "GET" }
    );
  },

  /** Create order from customer web checkout (no staff token). */
  customerCreateOrder: function (body) {
    return jsonFetch("/customer/order-create/", {
      method: "POST",
      body: JSON.stringify(body || {})
    });
  },

  /** Start Cashfree session for a website order. */
  customerPaymentInit: function (body) {
    return jsonFetch("/customer/payment-init/", {
      method: "POST",
      body: JSON.stringify(body || {})
    });
  },
  customerPaylinkResend: function (body) {
    return jsonFetch("/customer/paylink-resend/", {
      method: "POST",
      body: JSON.stringify(body || {})
    });
  }
};
